import React from "react";
import { useLocation } from "react-router-dom";
import { ListSkeleton } from "./ListSkeleton";

// ── Primitive ────────────────────────────────────────────────────────────────

interface SkeletonProps {
  className?: string;
  style?: React.CSSProperties;
}

/** Single shimmering placeholder block — size/shape/radius driven entirely by className. */
export const Skeleton: React.FC<SkeletonProps> = ({ className = "", style }) => (
  <div className={`shimmer ${className}`} style={style} aria-hidden="true" />
);

// ── App shell (icon rail) ──────────────────────────────────────────────────

/** Static (non-shimmering) stand-in for AppLayout's icon rail — this chrome
 * renders instantly once its chunk loads, so only real content below needs
 * a loading treatment. */
function IconRailSkeleton() {
  return (
    <nav
      className="hidden sm:flex w-16 shrink-0 flex-col items-center py-4 border-r bg-base/80 border-edge z-20"
      aria-hidden="true"
    >
      <div className="mb-6 w-5 h-5 rounded bg-edge/70" />
      <div className="flex flex-col items-center gap-2 flex-1 w-full px-1.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="w-11 h-11 rounded-xl bg-edge/40" />
        ))}
      </div>
      <div className="flex flex-col items-center gap-2 w-full px-2">
        <div className="w-8 h-8 rounded-lg bg-edge/40" />
        <div className="w-8 h-8 rounded-lg bg-edge/40" />
        <div className="w-8 h-8 rounded-full bg-edge/70" />
      </div>
    </nav>
  );
}

/** Wraps page-specific skeleton content in the same shell shape as AppLayout
 * (icon rail on desktop, bottom tab bar on mobile), so navigating into a
 * not-yet-loaded authenticated page never flashes a blank screen. */
export const AppShellSkeleton: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="h-screen flex overflow-hidden bg-base">
    <IconRailSkeleton />
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-hidden">{children}</div>
      <nav
        className="sm:hidden shrink-0 h-14 border-t border-edge bg-base/95 flex items-center justify-around px-4"
        aria-hidden="true"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="w-8 h-8 rounded-lg bg-edge/40" />
        ))}
      </nav>
    </div>
  </div>
);

// ── Standalone topbar shell (Profile / Providers) ───────────────────────────

export const StandaloneShellSkeleton: React.FC = () => (
  <div className="min-h-screen bg-base">
    <header className="h-14 border-b border-edge bg-surface flex items-center px-6 gap-4" aria-hidden="true">
      <div className="w-6 h-6 rounded bg-edge/50" />
      <div className="w-px h-5 bg-edge" />
      <div className="w-4 h-4 rounded bg-edge/50" />
      <Skeleton className="h-3 w-24 rounded" />
    </header>
    <main className="max-w-2xl mx-auto px-6 py-10 space-y-6">
      <div className="rounded-xl border border-edge bg-surface p-6 flex items-center gap-5">
        <Skeleton className="w-14 h-14 rounded-full shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3 w-40 rounded" />
          <Skeleton className="h-2.5 w-56 rounded" />
        </div>
      </div>
      <div className="rounded-xl border border-edge bg-surface p-6 space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between py-2">
            <Skeleton className="h-2.5 w-16 rounded" style={{ animationDelay: `${i * 80}ms` }} />
            <Skeleton className="h-2.5 w-32 rounded" style={{ animationDelay: `${i * 80}ms` }} />
          </div>
        ))}
      </div>
    </main>
  </div>
);

// ── Page content shapes ──────────────────────────────────────────────────────

/** Mirrors ChatPage/ADKChatView's real shape: a full-width "Orchy" topbar, then
 * a left chat column (header + centered empty-state + input pill) beside a
 * right graph panel (Graph/Artifacts/Timeline header + empty-state), matching
 * ADKChatView.tsx/ADKChatRightPanel.tsx rather than a generic bubble stack. */
export const ChatPageSkeleton: React.FC = () => (
  <div className="h-full flex flex-col overflow-hidden">
    <div className="h-14 shrink-0 border-b border-edge bg-surface flex items-center px-5" aria-hidden="true">
      <Skeleton className="h-3 w-14 rounded" />
    </div>
    <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
      {/* Left: chat column */}
      <div className="flex flex-col overflow-hidden shrink-0 border-r border-edge lg:w-[42%] lg:min-w-55">
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-edge shrink-0" aria-hidden="true">
          <div className="flex items-center gap-2">
            <div className="w-3.5 h-3.5 rounded bg-edge/50" />
            <Skeleton className="h-3 w-16 rounded" />
          </div>
          <Skeleton className="h-5 w-12 rounded-md" />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-3 px-8" aria-hidden="true">
          <Skeleton className="w-12 h-12 rounded-2xl" />
          <Skeleton className="h-3 w-32 rounded" />
          <Skeleton className="h-2.5 w-48 rounded" style={{ animationDelay: "80ms" }} />
          <Skeleton className="h-2.5 w-40 rounded" style={{ animationDelay: "160ms" }} />
        </div>
        <div className="shrink-0 border-t border-edge px-3 py-3">
          <Skeleton className="h-9 w-full rounded-3xl" />
        </div>
      </div>
      {/* Right: graph panel */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-edge shrink-0" aria-hidden="true">
          <div className="flex items-center gap-1.5">
            <Skeleton className="h-5 w-14 rounded-md" />
            <Skeleton className="h-5 w-16 rounded-md" />
          </div>
          <Skeleton className="h-5 w-16 rounded-md" />
        </div>
        <div className="flex-1 flex items-center justify-center" aria-hidden="true">
          <Skeleton className="w-7 h-7 rounded-full" />
        </div>
      </div>
    </div>
  </div>
);

/** Mirrors HistoryPage/AgentsPage: an optional header bar above a shimmering list. */
export const ListPageSkeleton: React.FC<{ variant?: "card" | "row"; withHeader?: boolean }> = ({
  variant = "card",
  withHeader = true,
}) => (
  <div className="h-full flex flex-col overflow-hidden">
    {withHeader && (
      <header className="px-6 py-4 border-b border-edge shrink-0 space-y-2" aria-hidden="true">
        <Skeleton className="h-3.5 w-40 rounded" />
        <Skeleton className="h-2.5 w-64 rounded" />
      </header>
    )}
    <div className="flex-1 overflow-hidden">
      <ListSkeleton count={6} variant={variant} />
    </div>
  </div>
);

/** Mirrors ConfigPage: a section nav rail beside a card of form-field placeholders. */
export const SettingsPageSkeleton: React.FC = () => (
  <div className="flex h-full overflow-hidden">
    <aside className="hidden md:flex w-60 shrink-0 border-r border-edge flex-col bg-surface py-2 px-2 gap-1" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg">
          <div className="w-4 h-4 rounded bg-edge/50 shrink-0" />
          <Skeleton className="h-2.5 flex-1 rounded" style={{ animationDelay: `${i * 80}ms` }} />
        </div>
      ))}
    </aside>
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div className="rounded-xl border border-edge bg-surface p-5 space-y-3 max-w-xl">
        <Skeleton className="h-3.5 w-32 rounded" />
        <Skeleton className="h-2.5 w-full rounded" />
        <Skeleton className="h-9 w-full rounded-lg mt-2" />
      </div>
      <div className="rounded-xl border border-edge bg-surface p-5 space-y-3 max-w-xl">
        <Skeleton className="h-3.5 w-40 rounded" />
        <Skeleton className="h-9 w-full rounded-lg" />
        <Skeleton className="h-9 w-full rounded-lg" />
      </div>
    </div>
  </div>
);

/** Modest fallback for public/unmatched routes (landing, 404) — those pages
 * either have their own rich content worth waiting for briefly, or don't
 * warrant a bespoke shape. */
export const GenericPageSkeleton: React.FC = () => (
  <div className="min-h-screen bg-base flex items-center justify-center px-6">
    <div className="w-full max-w-md space-y-3">
      <Skeleton className="h-4 w-2/3 mx-auto rounded" />
      <Skeleton className="h-3 w-1/2 mx-auto rounded" />
    </div>
  </div>
);

// ── Router-aware picker ──────────────────────────────────────────────────────

/**
 * Chooses a page-shaped shimmer skeleton from the current URL so route-level
 * Suspense fallbacks and auth loading gates never render a blank screen.
 * Used by App.tsx (Suspense fallback + top-level auth gate) and ProtectedRoute.
 */
export const RouteSkeleton: React.FC = () => {
  const { pathname } = useLocation();

  if (pathname.startsWith("/chat"))    return <AppShellSkeleton><ChatPageSkeleton /></AppShellSkeleton>;
  if (pathname.startsWith("/agents"))  return <AppShellSkeleton><ListPageSkeleton variant="card" withHeader={false} /></AppShellSkeleton>;
  if (pathname.startsWith("/history")) return <AppShellSkeleton><ListPageSkeleton variant="row" /></AppShellSkeleton>;
  if (pathname.startsWith("/config"))  return <AppShellSkeleton><SettingsPageSkeleton /></AppShellSkeleton>;
  if (pathname.startsWith("/profile") || pathname.startsWith("/providers")) return <StandaloneShellSkeleton />;
  return <GenericPageSkeleton />;
};
